import { GameOverGameState, TablePlayerModelV2 } from 'shared';
import { Modal } from '../../ui/Modal';
import { Button } from '../../ui/Button';
import { token } from '../../ui/theme';

interface Props {
  open: boolean;
  gameState: GameOverGameState;
  tablePlayers: (TablePlayerModelV2 | undefined)[];
  currentPlayerId: string;
  onReady: () => void;
}

export const GameOverModal = ({
  open,
  gameState,
  tablePlayers,
  currentPlayerId,
  onReady,
}: Props) => {
  const { teams, scoreToWin, winningTeamId, readyPlayerIds, setHistory } = gameState;

  const teamLabel = (teamId: string) => {
    const idx = teams.findIndex((t) => t.id === teamId);
    return idx >= 0 ? `Team ${idx + 1}` : '?';
  };

  const playerName = (playerId: string) =>
    tablePlayers.find((p) => p?.id === playerId)?.name ?? 'Unknown';

  // Only human players need to click ready; computers are auto-ready.
  const humanPlayers = tablePlayers.filter((p): p is TablePlayerModelV2 => !!p && !p.isComputer);
  const alreadyReady = readyPlayerIds.includes(currentPlayerId);

  return (
    <Modal
      open={open}
      title={
        <span style={{ color: token('color-gold'), letterSpacing: 2 }}>
          🏆 {teamLabel(winningTeamId)} Wins
        </span>
      }
      actions={
        <Button
          variant="primary"
          disabled={alreadyReady}
          onClick={onReady}
          style={
            alreadyReady
              ? undefined
              : { background: token('color-win'), color: token('color-text') }
          }
        >
          {alreadyReady ? 'Waiting…' : "I'm Ready"}
        </Button>
      }
    >
      {/* Final scores */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 20 }}>
        {teams.map((team, i) => {
          const isWinner = team.id === winningTeamId;
          return (
            <div
              key={team.id}
              style={{
                flex: 1,
                background: token('color-surface'),
                border: `1px solid ${isWinner ? token('color-gold') : token('color-border')}`,
                borderRadius: 8,
                padding: '10px 14px',
                textAlign: 'center',
              }}
            >
              <div
                style={{
                  color: token('color-muted'),
                  fontSize: token('font-size-xs'),
                  letterSpacing: 1,
                  marginBottom: 'var(--space-1)',
                }}
              >
                TEAM {i + 1}
              </div>
              <span
                style={{
                  color: isWinner ? token('color-gold') : token('color-text'),
                  fontWeight: 700,
                  fontSize: token('font-size-lg'),
                }}
              >
                {team.score}
              </span>
              <span
                style={{
                  color: token('color-muted'),
                  fontSize: token('font-size-xs'),
                  marginLeft: 3,
                }}
              >
                / {scoreToWin}
              </span>
            </div>
          );
        })}
      </div>

      {/* Set history */}
      <div
        style={{
          color: token('color-muted'),
          fontSize: token('font-size-xs'),
          letterSpacing: 1,
          marginBottom: 'var(--space-2)',
        }}
      >
        SET HISTORY
      </div>
      <div
        style={{
          background: token('color-surface'),
          border: `1px solid ${token('color-border')}`,
          borderRadius: 8,
          overflow: 'hidden',
        }}
      >
        {setHistory.length === 0 && (
          <div style={{ color: token('color-muted'), padding: '10px 14px' }}>No sets played.</div>
        )}
        {setHistory.map((entry, i) => (
          <div
            key={i}
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '8px 14px',
              borderTop: i === 0 ? 'none' : `1px solid ${token('color-border')}`,
              fontSize: token('font-size-xs'),
            }}
          >
            <span style={{ color: token('color-muted') }}>#{i + 1}</span>
            <span style={{ color: token('color-text'), flex: 1, margin: '0 10px' }}>
              {teamLabel(entry.biddingTeamId)} ({playerName(entry.biddingPlayerId)}) bid{' '}
              {entry.bidForSet}{' '}
              <span
                style={{
                  color: entry.madeBid ? token('color-win') : token('color-lose'),
                  fontWeight: 600,
                }}
              >
                {entry.madeBid ? 'made it' : 'set'}
              </span>
            </span>
            <span style={{ color: token('color-muted'), whiteSpace: 'nowrap' }}>
              {entry.scores.map((s) => s.score).join(' – ')}
            </span>
          </div>
        ))}
      </div>

      {/* Readiness */}
      <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
        {humanPlayers.map((p) => {
          const ready = readyPlayerIds.includes(p.id);
          return (
            <div
              key={p.id}
              style={{
                flex: 1,
                background: token('color-surface'),
                border: `1px solid ${ready ? token('color-win') : token('color-border')}`,
                borderRadius: 8,
                padding: '8px 10px',
                textAlign: 'center',
                fontSize: token('font-size-xs'),
              }}
            >
              <div style={{ color: token('color-text') }}>{p.name}</div>
              <div
                style={{
                  color: ready ? token('color-win') : token('color-muted'),
                  fontWeight: 600,
                  marginTop: 2,
                }}
              >
                {ready ? '✓ Ready' : 'waiting…'}
              </div>
            </div>
          );
        })}
      </div>
    </Modal>
  );
};
